import axiosInstant from '../../../api';
import {
  createProduct,
  createProductForBrand,
  patchProductForBrand,
  updateProduct,
} from '../../../api/endpoints';
import { uploadImage } from '../../../pages/profile/helper';
import { ImageProps, IProduct, ISchemaValues } from './constants';

export const uploadSingleImage = (image: ImageProps, userId?: number) => {
  if (image.file && image.extension) {
    return uploadImage(image.file, userId || Math.random(), image.extension);
  }
  return undefined;
};

export const getFormattedProduct = (product?: IProduct, brandId?: number) => {
  const rmnBrands = product?.brands?.filter((brand) => brand?.rmn === true) || [];
  // `!rmn` (not `rmn === false`) so brands with a null flag count as regular.
  const regularBrand = product?.brands?.find((brand) => !brand?.rmn);
  // When editing, the product's own brand wins — the caller-context brandId is
  // the *viewer's* brand (an RMN for network users) and must not displace it.
  // Products whose only brands are RMN-flagged fall back to the first one so
  // the field isn't empty and an untouched save round-trips the same brands.
  const mainBrand = regularBrand || (product ? rmnBrands[0] : undefined);
  return {
    productName: product?.title || '',
    productDescription: product?.description || product?.short_description || '',
    brand_id: product
      ? mainBrand?.id?.toString() || ''
      : brandId?.toString() || '',
    brand_name: mainBrand?.name || '',
    rmn_ids: rmnBrands.map((b) => b.id),
    url: product?.url || '',
    product_category_ids: product?.product_categories?.map((c) => c.id) || [],
    estimated_annual_units: product?.estimated_annual_units != null ? String(product.estimated_annual_units) : '',
    estimated_annual_returns: product?.estimated_annual_returns != null ? String(product.estimated_annual_returns) : '',
    disclaimers: product?.disclaimers || '',
  };
};

// '' -> null (clear), otherwise a non-negative integer; anything else is
// left out of the payload so a stray character never overwrites the value.
export const toEstimate = (raw?: string): number | null | undefined => {
  if (raw == null) return undefined;
  const trimmed = String(raw).trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n);
};

export const getBrandId = (values: ISchemaValues): number => {
  // Brand creation is owned by the create-brand modal; by submit time the brand
  // is already persisted and brand_id holds its real numeric id.
  const id = Number(values.brand_id);
  if (!id || Number.isNaN(id)) throw new Error('Brand is required');
  return id;
};

export const getValidUrl = (
  isInternalUser: boolean,
  brandId: string | number,
  productId?: string | number
) => {
  if (isInternalUser) {
    if (productId) return updateProduct(Number(productId));
    return createProduct();
  }
  if (productId)
    return patchProductForBrand(brandId.toString(), productId.toString());
  return createProductForBrand(brandId.toString());
};

interface ApiCallProps {
  isInternalUser: boolean;
  payload: any;
  productId?: string | number;
}

export const apiCall = ({
  isInternalUser,
  payload,
  productId,
}: ApiCallProps) => {
  if (!payload?.brands?.length) throw new Error('Brand is missing');

  return axiosInstant.request({
    url: getValidUrl(isInternalUser, payload.brands[0].id, productId),
    method: productId ? 'PATCH' : 'POST',
    data: payload,
  });
};

export const getRMNOptions = async () => {
  const response = await axiosInstant.get('/brands?rmn=true&limit=100');
  return response.data.rows.map((brand: any) => ({
    label: brand.name,
    value: brand.id,
  }));
};
